/** Maps file extension → canonical MIME type. Single source of truth used by
 *  useFetchDataRoomFiles (MIME resolution) and DocumentViewer (render routing). */
export const EXT_MIME: Record<string, string> = {
    pdf: 'application/pdf',
    xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    xls: 'application/vnd.ms-excel',
    csv: 'text/csv',
    docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    txt: 'text/plain',
    // Images — needed for extension-based fallback when server returns application/octet-stream
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    svg: 'image/svg+xml',
};

/** Resolve a canonical MIME type from a filename's extension. `fallbackExt` is
 *  used when the name has no usable extension (and as the final default when the
 *  extension isn't in EXT_MIME). Returns '' when nothing resolves. */
export function getMimeTypeFromFileName(fileName?: string, fallbackExt = ''): string {
    const ext = fileName?.split('.').pop()?.toLowerCase() || fallbackExt;
    return EXT_MIME[ext] ?? EXT_MIME[fallbackExt] ?? '';
}

/** All MIME types that open in the preview drawer instead of downloading.
 *  text/plain is deliberately excluded: DocumentViewer has no generic text
 *  renderer (its unknown-mime fallback is the PDF viewer, which breaks on
 *  plain text). Every .txt open path routes through its own explicit
 *  DocxDrawer branch BEFORE any previewable check, so txt never needs to be
 *  generically previewable. */
export const PREVIEWABLE_MIME_TYPES = [
    'application/pdf',
    'application/csv',
    ...Object.values(EXT_MIME).filter((mime) => mime !== EXT_MIME.txt),
];

/** MIME types routed to the read-only Fortune-Sheet SheetView. */
export const SPREADSHEET_TYPES = [EXT_MIME.xlsx, EXT_MIME.xls, EXT_MIME.csv, 'application/csv'];

/** MIME types routed to the read-only DocxViewer. Legacy `.doc` is intentionally
 *  absent — it stays unsupported (download-only) in the viewer. */
export const DOCX_TYPES = [EXT_MIME.docx];

/** Shared state shape for components that preview blobs in FileViewerDrawer. */
export interface BlobViewerState {
    url: string;
    blob: Blob;
}

/** Create a blob URL for preview, revoking any previous one. */
export function openBlobViewer(blob: Blob, previousUrl?: string | null): BlobViewerState {
    if (previousUrl) URL.revokeObjectURL(previousUrl);
    return { url: URL.createObjectURL(blob), blob };
}

/** Revoke the blob URL and return null (for clearing state). */
export function closeBlobViewer(state: BlobViewerState | null): null {
    if (state?.url) URL.revokeObjectURL(state.url);
    return null;
}
